// PLACEHOLDER (B16): the neutral reward machine, a treasure chest, for trips with no kit or a kit
// that names no machine. Plain shapes that prove the swap end to end (words, 2D cabinet, the
// round's header, the sticker and prize openings); the real look waits for the owner's pick from
// docs/renders/machines/m01. The 3D cabinet is cabinet3d.ts (stage chunk).
import { motion, useAnimate } from 'motion/react'
import { useState } from 'react'
import { buzz } from '../../../audio/haptics'
import { appSound } from '../../../audio/appSounds'
import { revealFanfare, unwrapPop, unwrapWhoosh } from '../../../audio/synth'
import { springs } from '../../../motion/springs'
import { t } from '../../../strings/en'
import { capped, type FlatMachineProps, type RevealProps, type RewardMachine } from '../types'

const BODY: Record<string, string> = { red: '#B4532A', mint: '#2E8C62', blue: '#3A5FA0' }

/** A chest: body, lid on a hinge (rotated by the caller), gold bands and a clasp. */
function ChestShape({ body, lid = 0, glow = false }: { body: string; lid?: number; glow?: boolean }) {
  return (
    <>
      {glow ? <ellipse cx="190" cy="196" rx="120" ry="26" fill="#FFE08A" /> : null}
      <rect x="70" y="196" width="240" height="150" rx="16" fill={body} stroke="#1B2A3A" strokeWidth="4" />
      <rect x="120" y="196" width="18" height="150" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="3" />
      <rect x="242" y="196" width="18" height="150" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="3" />
      <rect x="172" y="200" width="36" height="44" rx="6" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="3" />
      <g className="lid" style={{ transformOrigin: '70px 196px' }} transform={`rotate(${lid} 70 196)`}>
        <path d="M70,196 v-40 a40,40 0 0 1 40,-40 h160 a40,40 0 0 1 40,40 v40 z" fill={body} stroke="#1B2A3A" strokeWidth="4" strokeLinejoin="round" />
        <rect x="120" y="116" width="18" height="80" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="3" />
        <rect x="242" y="116" width="18" height="80" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="3" />
      </g>
    </>
  )
}

function Chest({ title, theme, locked, tier, label, onDropped }: FlatMachineProps) {
  const [scope, animate] = useAnimate()
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const still = tier === 'still'

  async function spend() {
    if (locked || busy) return
    setBusy(true)
    buzz('drop')
    chest.sounds.spend?.()
    if (!still) await capped(async () => { await animate('.chest-body', { rotate: [0, -3, 3, -2, 0] }, { duration: 0.35 }) }, 1000)
    setOpen(true)
    buzz('crack')
    chest.sounds.open?.()
    if (!still) await new Promise((r) => setTimeout(r, 300))
    setBusy(false)
    setOpen(false)
    onDropped()
  }

  return (
    <div ref={scope} className="gashapon chest-machine" data-placeholder="chest">
      <svg viewBox="0 0 380 420" role="img" aria-label={`${title} ${t.machines.chest.cabinet}${locked ? ', locked' : ''}`} onClick={() => void spend()}>
        <ellipse cx="190" cy="372" rx="140" ry="12" fill="#000" opacity=".25" />
        <g className="chest-body" style={{ transformOrigin: '190px 346px', ...(locked ? { filter: 'saturate(.4) brightness(.85)' } : {}) }}>
          <ChestShape body={BODY[theme.color] ?? BODY.red!} lid={open ? -70 : 0} glow={open} />
        </g>
        <rect x="100" y="380" width="180" height="30" rx="10" fill="#fff" stroke="#1B2A3A" strokeWidth="3" />
        <text x="190" y="401" textAnchor="middle" fontFamily="Fredoka" fontWeight="700" fontSize={title.length > 13 ? 15 : 18} fill="#1B2A3A">
          {title.toUpperCase()}
        </text>
        <text x="370" y="20" textAnchor="end" fontFamily="Fredoka" fontSize="11" fill="#1B2A3A" opacity=".5">
          PLACEHOLDER
        </text>
      </svg>
      <button className="btn y press twist-btn" disabled={locked || busy} onClick={() => void spend()} hidden={locked}>
        {label}
      </button>
    </div>
  )
}

/** The reveal (WI-13): the chest opens straight onto what the twist paid (flat: a placeholder look). */
function Reveal({ look, still }: RevealProps) {
  return (
    <>
      <motion.svg className="prize-capsule" viewBox="60 100 260 260" overflow="visible" aria-hidden initial={still ? false : { scale: 0.3 }} animate={{ scale: 1 }} transition={springs.slap}>
        <ChestShape body={look.colour} lid={-70} glow />
      </motion.svg>
      <motion.span className={`prize-big${look.gold ? ' gold' : ''}`} aria-hidden initial={still ? false : { scale: 0, y: 40 }} animate={{ scale: 1, y: 0 }} transition={{ delay: still ? 0 : 0.35, ...springs.bounce }}>
        {look.art ? <img src={look.art} alt="" /> : look.kind === 'key' ? '🔑' : look.emoji}
      </motion.span>
    </>
  )
}

export const chest: RewardMachine = {
  id: 'chest',
  placeholder: true,
  words: t.machines.chest,
  Flat: Chest,
  Reveal,
  // S7: Stable Audio Open takes when picked (a creak, a thunk), the synth until then.
  sounds: { spend: () => appSound('chest_creak', unwrapWhoosh), open: () => appSound('chest_thunk', unwrapPop), reveal: revealFanfare },
  haptics: { spend: 'drop', open: 'crack' },
}
